import { type PaletteOptions } from "@mui/material";

// Neutrals
const WHITE_COLOR = "#ffffff";
const BLACK_COLOR = "#000000";

// Lemon
const LEMON_100_COLOR = "#FAFAAB";
const LEMON_200_COLOR = "#FFFF80";
const LEMON_300_COLOR = "#F8F831";
const LEMON_400_COLOR = "#E6E610";
// const LEMON_500_COLOR = "#EFEF0E";
// const LEMON_600_COLOR = "#C1B638";
// const LEMON_700_COLOR = "#AD8F29";
// const LEMON_800_COLOR = "#968F17";
// const LEMON_900_COLOR = "#7F7D00";

// Raspberry
const RASPBERRY_100_COLOR = "#FDE9EA";
// const RASPBERRY_200_COLOR = "#FCC8CC";
const RASPBERRY_300_COLOR = "#F9A5AD";
// const RASPBERRY_400_COLOR = "#EE828F";
const RASPBERRY_500_COLOR = "#DF5B70";
const RASPBERRY_600_COLOR = "#CA3051";
const RASPBERRY_700_COLOR = "#A82A44";
const RASPBERRY_800_COLOR = "#862335";
// const RASPBERRY_900_COLOR = "#631A27";

// Stone
const STONE_50_COLOR = "#FFFEFC";
const STONE_100_COLOR = "#F6F5F3";
const STONE_200_COLOR = "#F4F2EC";
const STONE_300_COLOR = "#ECE7DC";
const STONE_400_COLOR = "#DDD8C9";
const STONE_500_COLOR = "#BFB3A4";
const STONE_600_COLOR = "#9F8D7F";
const STONE_700_COLOR = "#7B645C";
const STONE_800_COLOR = "#5D403C";
const STONE_900_COLOR = "#37171A";
const STONE_950_COLOR = "#210E10";

// Coral
const CORAL_100_COLOR = "#FFE8D7";
const CORAL_300_COLOR = "#FDCCA6";
const CORAL_500_COLOR = "#F69551";
const CORAL_700_COLOR = "#CA7236";
const CORAL_900_COLOR = "#904A19";

// Azure
const AZURE_100_COLOR = "#D0EEFF";
const AZURE_300_COLOR = "#A3D8FA";
const AZURE_500_COLOR = "#4DBEF5";
const AZURE_700_COLOR = "#2E96C6";
const AZURE_900_COLOR = "#00658A";

// Violet
const VIOLET_100_COLOR = "#E1D4FF";
const VIOLET_300_COLOR = "#CCB8FF";
const VIOLET_500_COLOR = "#A672F7";
// const VIOLET_700_COLOR = "#925FE9";
const VIOLET_900_COLOR = "#6A2CC3";

// Clover
const CLOVER_100_COLOR = "#D2F2D2";
// const CLOVER_300_COLOR = "#7DE085";
const CLOVER_500_COLOR = "#24C23F";
const CLOVER_700_COLOR = "#249A34";
const CLOVER_900_COLOR = "#206F29";

// Ember
// const EMBER_100_COLOR = "#FFD9D2";
// const EMBER_300_COLOR = "#FF9A87";
// const EMBER_500_COLOR = "#F6524C";
// const EMBER_700_COLOR = "#D03630";
// const EMBER_900_COLOR = "#C7312C";

export const generateSpinnerGradient = (hexColor: string) =>
  `conic-gradient(from 90deg at 50% 50%, ${hexColor}00 0deg, ${hexColor}0A 0.04deg, ${hexColor}FF 360deg)`;

declare module "@mui/material/styles" {
  interface IntentColorsMap {
    /**
     * This is called `surface` in figma
     */
    background: string;
    /**
     * This is called `surfaceInvert` in figma
     */
    backgroundInvert: string;
    border: string;
    text: string;
    icon: string;
    gradient?: string;
  }

  // So far these are optional as module augmentation will affect the old theme and I want to avoid confusions
  interface CustomPalette {
    brand: {
      main: string;
      light: string;
      dark: string;
      contrastText: string;
    };
    border?: {
      primary: string;
      dark: string;
      subtle: string;
      primaryInvert: string;
      subtleInvert: string;
      darkInvert: string;
      main: string;
    };
    icon?: {
      primary: string;
      disabled: string;
      lightInvert: string;
    };
    intent?: {
      neutral: IntentColorsMap;
      info: IntentColorsMap;
      success: IntentColorsMap;
      warning: IntentColorsMap;
      error: IntentColorsMap;
      disabled: IntentColorsMap;
    };
    instantPay?: {
      background: string;
      text: string;
    };

    /**
     * This is intentionally exported separately so in the future we have the possibility to split
     * base design system from hcp app.
     *
     * If we were to continue using this design system for other apps, we could easily maintain a
     * global one with generic colors and only export HCP and shifts specific stuff separately.
     */
    shift?: {
      urgent: IntentColorsMap & {
        gradient: string;
      };
      inAppAds: {
        text: string;
        background: string;
        timeSlotBackground: string;
      };
      bookingRequest: {
        text: string;
        background: string;
      };
      priority: IntentColorsMap & {
        gradient: string;
      };
      invite: IntentColorsMap & {
        gradient: string;
      };
      block: IntentColorsMap & {
        gradient: string;
      };
      day: {
        gradient: string;
      };
      evening: {
        gradient: string;
      };
      overnight: {
        gradient: string;
      };
      verified: {
        gradient: string;
      };
      unverified: {
        gradient: string;
      };
      inreview: {
        gradient: string;
      };
      badgeSkippedWarning: {
        gradient: string;
        warningColor: string;
      };
      badgePolicy: {
        successGradient: string;
      };
      transferable: {
        background: string;
        text: string;
      };
      bundle: {
        border: string;
        gradient: string;
      };
      double: {
        border: string;
        gradient: string;
        footerBackground: string;
        footerText: string;
      };
      forYou: {
        background: string;
        border: string;
      };
      history: {
        gradient: string;
      };
    };
    favoriteWorkplaces?: {
      background: string;
    };
    link?: {
      primary: string;
    };
    charting?: {
      review: {
        gradient: string;
      };
      recall: {
        gradient: string;
      };
      violation: {
        gradient: string;
      };
    };
    placement?: {
      feature: {
        gradient: string;
        background: string;
      };
      openHouse: {
        feature: {
          gradient: string;
          background: string;
        };
        gradient: string;
        border: string;
        text: string;
      };
      interviewDay: {
        feature: {
          gradient: string;
          background: string;
        };
      };
      apply: {
        feature: {
          background: string;
        };
      };
    };
    documents?: {
      neutral: {
        gradient: string;
      };
      warning: {
        gradient: string;
      };
      error: {
        gradient: string;
      };
    };
    onboarding?: {
      inProgress: {
        gradient: string;
      };
      error: {
        gradient: string;
      };
      neutral: {
        gradient: string;
      };
    };
    homeHealth?: {
      visit: {
        background: string;
        text: string;
        gradient: string;
      };
      advertisement: {
        text: string;
        gradient: string;
      };
    };
    arrivalTime?: {
      accent: string;
    };
    payment?: {
      background: string;
      text: string;
    };
    components?: {
      locationMarker: {
        background: string;
        foreground: string;
        border: string;
      };
      clusterMarker: {
        background: string;
        foreground: string;
      };
      spinner: {
        primary: string;
        secondary: string;
        destructive: string;
        invert: string;
        generateGradient: (hexColor: string) => string;
      };
      avatar: {
        primary: undefined;
        secondary: string;
        tertiary: string;
        quaternary: string;
        septenary: string;
      };
      circleProgress: {
        track: string;
        primary: string;
        secondary: string;
        warning: string;
      };
      holdToConfirmButton: {
        default: string;
        confirmed: string;
      };
    };
    banner?: {
      borderGradient: {
        background: string;
        border: string;
      };
    };
    legacyFullScreenDialogBackground: string;
    workplaceReferrals?: {
      referralGuide: {
        gradient: string;
      };
      stats: {
        success: {
          gradient: string;
        };
        pending: {
          gradient: string;
        };
      };
    };
    workplaceGroupChats?: {
      gradient: string;
    };
    cancellationProtection?: {
      gradient: string;
    };
  }

  // Actual MUI types
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Palette extends CustomPalette {}
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface PaletteOptions extends CustomPalette {}

  interface TypeText {
    primary: string;
    secondary: string;
    tertiary?: string;
    invertPrimary?: string;
    invertSecondary?: string;
    invertTertiary?: string;
  }

  interface TypeBackground {
    default: string;
    paper: string;
    primary: string;
    secondary: string;
    secondaryDark?: string;
    tertiary?: string;
    quaternary?: string;
    invert?: string;
  }
}

export const muiRebrandThemePalette: PaletteOptions = {
  brand: {
    main: LEMON_300_COLOR,
    dark: LEMON_400_COLOR,
    light: LEMON_200_COLOR,
    contrastText: STONE_950_COLOR,
  },
  primary: {
    main: STONE_950_COLOR,
    contrastText: WHITE_COLOR,
    dark: BLACK_COLOR,
    light: STONE_900_COLOR,
  },
  secondary: {
    main: STONE_50_COLOR,
    contrastText: STONE_950_COLOR, // not used in codebase - based on Claude search
  },
  error: {
    main: RASPBERRY_700_COLOR,
    light: RASPBERRY_600_COLOR,
    dark: RASPBERRY_800_COLOR,
    contrastText: WHITE_COLOR,
  },
  info: {
    main: STONE_900_COLOR,
    light: STONE_200_COLOR,
    dark: STONE_950_COLOR,
    contrastText: WHITE_COLOR, // not used in codebase - based on Claude search
  },
  warning: {
    main: CORAL_700_COLOR,
    light: CORAL_100_COLOR, // not used in codebase - based on Claude search
    dark: CORAL_300_COLOR,
    contrastText: WHITE_COLOR, // not used in codebase - based on Claude search
  },
  success: {
    main: CLOVER_700_COLOR,
    light: CLOVER_500_COLOR, // not used in codebase - based on Claude search
    dark: CLOVER_900_COLOR,
    contrastText: WHITE_COLOR,
  },
  background: {
    default: STONE_100_COLOR,
    paper: STONE_100_COLOR,
    primary: STONE_100_COLOR,
    secondary: STONE_200_COLOR,
    secondaryDark: STONE_300_COLOR,
    tertiary: WHITE_COLOR,
    quaternary: STONE_50_COLOR,
    invert: STONE_950_COLOR,
  },
  text: {
    primary: STONE_950_COLOR,
    secondary: `${STONE_950_COLOR}B3`,
    tertiary: `${STONE_950_COLOR}99`,
    disabled: `${STONE_950_COLOR}66`,
    invertPrimary: WHITE_COLOR,
    invertSecondary: STONE_100_COLOR, // not used in codebase - based on Claude search
    invertTertiary: STONE_200_COLOR, // not used in codebase - based on Claude search
  },
  action: {
    active: STONE_950_COLOR,
  },
  divider: STONE_200_COLOR,
  border: {
    primary: `${STONE_950_COLOR}33`,
    dark: `${STONE_950_COLOR}66`,
    subtle: `${STONE_950_COLOR}26`,
    primaryInvert: STONE_800_COLOR,
    subtleInvert: STONE_700_COLOR,
    darkInvert: STONE_500_COLOR,
    main: `${STONE_950_COLOR}33`,
  },
  icon: {
    primary: STONE_950_COLOR,
    disabled: STONE_400_COLOR, // not used in codebase - based on Claude search
    lightInvert: WHITE_COLOR,
  },
  intent: {
    neutral: {
      background: STONE_400_COLOR,
      backgroundInvert: STONE_900_COLOR, // not used in codebase - based on Claude search
      border: STONE_400_COLOR,
      text: STONE_900_COLOR,
      icon: STONE_900_COLOR,
      gradient: STONE_400_COLOR,
    },
    info: {
      background: STONE_400_COLOR, // not used in codebase - based on Claude search
      backgroundInvert: STONE_900_COLOR, // not used in codebase - based on Claude search
      border: STONE_400_COLOR, // not used in codebase - based on Claude search
      text: STONE_900_COLOR, // not used in codebase - based on Claude search
      icon: STONE_900_COLOR, // not used in codebase - based on Claude search
      gradient: STONE_400_COLOR,
    },
    success: {
      background: CLOVER_100_COLOR,
      backgroundInvert: CLOVER_700_COLOR, // not used in codebase - based on Claude search
      border: CLOVER_500_COLOR,
      text: CLOVER_900_COLOR,
      icon: CLOVER_900_COLOR,
      gradient: CLOVER_100_COLOR,
    },
    warning: {
      background: CORAL_100_COLOR,
      backgroundInvert: CORAL_700_COLOR,
      border: CORAL_500_COLOR,
      text: CORAL_900_COLOR,
      icon: CORAL_900_COLOR,
      gradient: CORAL_100_COLOR,
    },
    error: {
      background: RASPBERRY_100_COLOR,
      backgroundInvert: RASPBERRY_700_COLOR,
      border: RASPBERRY_500_COLOR,
      text: RASPBERRY_600_COLOR,
      icon: RASPBERRY_600_COLOR,
      gradient: RASPBERRY_100_COLOR,
    },
    disabled: {
      background: STONE_200_COLOR,
      backgroundInvert: STONE_300_COLOR,
      border: STONE_200_COLOR,
      text: STONE_600_COLOR,
      icon: STONE_600_COLOR,
    },
  },
  instantPay: {
    background: CLOVER_100_COLOR,
    text: CLOVER_900_COLOR,
  },
  shift: {
    urgent: {
      background: CORAL_100_COLOR,
      backgroundInvert: CORAL_700_COLOR, // not used in codebase - based on Claude search
      border: CORAL_500_COLOR,
      text: CORAL_900_COLOR,
      icon: CORAL_900_COLOR,
      gradient: CORAL_100_COLOR, // not used in codebase - based on Claude search
    },
    inAppAds: {
      text: STONE_900_COLOR, // not used in codebase - based on Claude search
      background: VIOLET_100_COLOR, // not sure about this usage
      timeSlotBackground: VIOLET_300_COLOR,
    },
    bookingRequest: {
      background: CORAL_100_COLOR,
      text: CORAL_700_COLOR,
    },
    priority: {
      background: VIOLET_100_COLOR,
      backgroundInvert: VIOLET_900_COLOR, // not used in codebase - based on Claude search
      border: VIOLET_300_COLOR,
      text: VIOLET_900_COLOR,
      icon: VIOLET_900_COLOR,
      gradient: VIOLET_100_COLOR, // not used in codebase - based on Claude search
    },
    invite: {
      background: VIOLET_100_COLOR,
      backgroundInvert: VIOLET_900_COLOR, // not used in codebase - based on Claude search
      border: VIOLET_300_COLOR,
      text: VIOLET_900_COLOR,
      icon: VIOLET_900_COLOR, // not used in codebase - based on Claude search
      gradient: VIOLET_100_COLOR, // not used in codebase - based on Claude search
    },
    block: {
      background: AZURE_100_COLOR,
      backgroundInvert: AZURE_700_COLOR, // not used in codebase - based on Claude search
      border: AZURE_700_COLOR,
      text: AZURE_900_COLOR,
      icon: AZURE_900_COLOR, // not used in codebase - based on Claude search
      gradient: AZURE_100_COLOR, // not used in codebase - based on Claude search
    },
    day: {
      gradient: CORAL_300_COLOR,
    },
    evening: {
      gradient: AZURE_300_COLOR,
    },
    forYou: {
      background: STONE_50_COLOR,
      border: `2px solid ${CLOVER_500_COLOR}`,
    },
    overnight: {
      gradient: VIOLET_300_COLOR,
    },
    transferable: {
      background: RASPBERRY_100_COLOR, // not used in codebase - based on Claude search
      text: RASPBERRY_700_COLOR, // not used in codebase - based on Claude search
    },
    bundle: {
      border: AZURE_500_COLOR,
      gradient: AZURE_100_COLOR,
    },
    double: {
      border: VIOLET_500_COLOR,
      gradient: VIOLET_100_COLOR,
      footerBackground: "transparent",
      footerText: VIOLET_900_COLOR,
    },
    history: {
      gradient: STONE_100_COLOR, // not sure about this usage
    },
    verified: {
      gradient: AZURE_100_COLOR, // not sure about this usage
    },
    unverified: {
      gradient: RASPBERRY_100_COLOR,
    },
    inreview: {
      gradient: CORAL_100_COLOR,
    },
    badgeSkippedWarning: {
      gradient: RASPBERRY_100_COLOR, // not used in codebase - based on Claude search
      warningColor: RASPBERRY_500_COLOR, // not used in codebase - based on Claude search
    },
    badgePolicy: {
      successGradient: CLOVER_100_COLOR, // not used in codebase - based on Claude search
    },
  },
  favoriteWorkplaces: {
    background: RASPBERRY_100_COLOR,
  },
  link: {
    primary: STONE_900_COLOR,
  },
  charting: {
    review: {
      gradient: CORAL_100_COLOR,
    },
    recall: {
      gradient: RASPBERRY_100_COLOR,
    },
    violation: {
      gradient: RASPBERRY_100_COLOR,
    },
  },
  placement: {
    feature: {
      gradient: VIOLET_100_COLOR,
      background: VIOLET_300_COLOR,
    },
    openHouse: {
      feature: {
        gradient: VIOLET_100_COLOR,
        background: VIOLET_500_COLOR, // not used in codebase - based on Claude search
      },
      gradient: STONE_50_COLOR, // not used in codebase - based on Claude search
      border: VIOLET_500_COLOR, // not used in codebase - based on Claude search
      text: VIOLET_900_COLOR, // not used in codebase - based on Claude search
    },
    interviewDay: {
      feature: {
        gradient: AZURE_100_COLOR,
        background: AZURE_500_COLOR, // not used in codebase - based on Claude search
      },
    },
    apply: {
      feature: {
        background: AZURE_300_COLOR,
      },
    },
  },
  documents: {
    neutral: {
      gradient: STONE_100_COLOR,
    },
    warning: {
      gradient: CORAL_100_COLOR,
    },
    error: {
      gradient: RASPBERRY_100_COLOR,
    },
  },
  onboarding: {
    inProgress: {
      gradient: CLOVER_100_COLOR,
    },
    error: {
      gradient: RASPBERRY_100_COLOR,
    },
    neutral: {
      gradient: CORAL_100_COLOR,
    },
  },
  payment: {
    background: CLOVER_100_COLOR, // not used in codebase
    text: CLOVER_900_COLOR, // not used in codebase
  },
  homeHealth: {
    visit: {
      // not used in codebase entirely
      background: AZURE_300_COLOR, // not used in codebase - based on Claude search
      text: AZURE_900_COLOR, // not used in codebase - based on Claude search
      gradient: AZURE_300_COLOR,
    },
    advertisement: {
      text: STONE_900_COLOR, // not used in codebase - based on Claude search
      gradient: STONE_100_COLOR, // not used in codebase - based on Claude search
    },
  },
  arrivalTime: {
    accent: RASPBERRY_500_COLOR,
  },
  components: {
    locationMarker: {
      background: `${RASPBERRY_300_COLOR}80`, // good place for branded color usage
      foreground: RASPBERRY_600_COLOR,
      border: WHITE_COLOR,
    },
    clusterMarker: {
      background: WHITE_COLOR,
      foreground: STONE_900_COLOR,
    },
    spinner: {
      primary: STONE_900_COLOR,
      secondary: STONE_500_COLOR,
      destructive: RASPBERRY_500_COLOR,
      invert: STONE_300_COLOR,
      generateGradient: generateSpinnerGradient,
    },
    avatar: {
      primary: undefined,
      secondary: STONE_200_COLOR,
      tertiary: CORAL_100_COLOR,
      quaternary: VIOLET_100_COLOR,
      septenary: RASPBERRY_100_COLOR,
    },
    circleProgress: {
      track: STONE_900_COLOR,
      primary: STONE_950_COLOR, // possible brand color usage
      secondary: STONE_900_COLOR,
      warning: RASPBERRY_600_COLOR,
    },
    holdToConfirmButton: {
      default: STONE_900_COLOR,
      confirmed: STONE_950_COLOR,
    },
  },
  legacyFullScreenDialogBackground: STONE_50_COLOR,
  workplaceReferrals: {
    referralGuide: {
      gradient: VIOLET_100_COLOR,
    },
    stats: {
      success: {
        gradient: CLOVER_100_COLOR,
      },
      pending: {
        gradient: AZURE_100_COLOR,
      },
    },
  },
  workplaceGroupChats: {
    gradient: LEMON_300_COLOR,
  },
  cancellationProtection: {
    gradient: LEMON_100_COLOR,
  },
};
